// Color presets surfaced in the Settings drawer. Each preset feeds the
// MUI theme `palette.primary` (and a matching dark/light pair). The keys
// here are the `themeColorPresets` values stored in the SettingsContext
// state.
//
// To add a new preset later, append a new entry below; the drawer UI
// reads from PRESETS_LIST so it picks new ones up automatically.
//
// FUTURE: swap this static catalog for a user-driven custom-color flow
// (hex picker / saved palettes). The shape of each preset matches what
// MUI expects so adding a custom preset is just constructing the same
// {main, light, dark, contrastText} object.

export const PRESETS = {
    default: {
        name: 'Default',
        labelAr: 'الافتراضي',
        main: '#00A76F',
        light: '#5BE49B',
        dark: '#007867',
        contrastText: '#FFFFFF',
    },
    cyan: {
        name: 'Cyan',
        labelAr: 'سماوي',
        main: '#078DEE',
        light: '#68CDF9',
        dark: '#0351AB',
        contrastText: '#FFFFFF',
    },
    purple: {
        name: 'Purple',
        labelAr: 'بنفسجي',
        main: '#7635DC',
        light: '#B985F4',
        dark: '#431A9E',
        contrastText: '#FFFFFF',
    },
    blue: {
        name: 'Blue',
        labelAr: 'أزرق',
        main: '#2065D1',
        light: '#76B0F1',
        dark: '#103996',
        contrastText: '#FFFFFF',
    },
    orange: {
        name: 'Orange',
        labelAr: 'برتقالي',
        main: '#FDA92D',
        light: '#FDD787',
        dark: '#B66A00',
        contrastText: '#1C252E',
    },
    red: {
        name: 'Red',
        labelAr: 'أحمر',
        main: '#FF3030',
        light: '#FF8080',
        dark: '#B71D1D',
        contrastText: '#FFFFFF',
    },
};

export const PRESETS_LIST = Object.keys(PRESETS);

// Sentinel preset key used when the user has picked a hex color from the
// custom-color dialog (see CustomColorDialog.jsx). The actual color value
// lives in `settings.themeCustomColor` and is fed through buildPresetFromHex
// at theme-build time.
export const CUSTOM_PRESET_KEY = 'custom';

export const getPreset = (key) => PRESETS[key] || PRESETS.default;

// Quick hex normaliser: accepts '#abc' / '#aabbcc' / 'aabbcc' / etc.
export const normaliseHex = (hex) => {
    if (typeof hex !== 'string') return null;
    let h = hex.trim().replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    return `#${h.toLowerCase()}`;
};

const hexToRgb = (hex) => {
    const h = normaliseHex(hex);
    if (!h) return null;
    return {
        r: parseInt(h.slice(1, 3), 16),
        g: parseInt(h.slice(3, 5), 16),
        b: parseInt(h.slice(5, 7), 16),
    };
};

const rgbToHex = ({ r, g, b }) =>
    '#' +
    [r, g, b]
        .map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0'))
        .join('');

const lightenChannel = (c, amount) => c + (255 - c) * amount;
const darkenChannel = (c, amount) => c * (1 - amount);

// Relative luminance of an sRGB color, used to decide whether the
// contrast text on top should be black or white.
const luminance = ({ r, g, b }) => {
    const toLin = (v) => {
        const x = v / 255;
        return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
};

/**
 * Derive a full preset {name, labelAr, main, light, dark, contrastText}
 * from a single hex. Returns null if the hex is invalid.
 */
export const buildPresetFromHex = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return null;
    const main = rgbToHex(rgb);
    const light = rgbToHex({
        r: lightenChannel(rgb.r, 0.4),
        g: lightenChannel(rgb.g, 0.4),
        b: lightenChannel(rgb.b, 0.4),
    });
    const dark = rgbToHex({
        r: darkenChannel(rgb.r, 0.35),
        g: darkenChannel(rgb.g, 0.35),
        b: darkenChannel(rgb.b, 0.35),
    });
    const contrastText = luminance(rgb) > 0.45 ? '#1C252E' : '#FFFFFF';
    return { name: 'Custom', labelAr: 'مخصص', main, light, dark, contrastText };
};
